import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Calendar, ChevronDown, Trash2, Plus } from "lucide-react";

// The year dropdown: pick a year, or add the next one. The Dashboard also lets you delete a year; other pages pass allowDelete={false}.
export default function YearSelector({ year, years, onYearChange, onAddYear, onDeleteYear, allowDelete = true, testIdPrefix = "" }) {
  const [open, setOpen] = useState(false);
  const nextYear = (years.length ? Math.max(...years) : year) + 1;

  const handleDelete = async (e, y) => {
    e.stopPropagation();
    if (!window.confirm(`Delete year ${y} and all its data? This cannot be undone.`)) return;
    await onDeleteYear(y);
    setOpen(false);
  };

  return (
    <div className="relative">
      <button data-testid={`${testIdPrefix}year-selector-button`} onClick={() => setOpen((o) => !o)}
        className="inline-flex items-center gap-2 rounded-full border border-[var(--hp-border-hi)] bg-[var(--hp-card)] px-4 py-2.5 text-sm font-semibold text-[var(--hp-heading)] hover:bg-[var(--hp-card-hover)] transition-colors duration-200">
        <Calendar className="h-4 w-4 text-[var(--hp-profit)]" /> {year}
        <ChevronDown className={`h-4 w-4 text-[var(--hp-muted)] transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      <AnimatePresence>
        {open && (
          <motion.div initial={{ opacity: 0, y: -6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -6 }}
            transition={{ duration: 0.15 }}
            className="absolute left-0 lg:left-auto lg:right-0 z-40 mt-2 w-52 max-w-[calc(100vw-2rem)] rounded-xl border border-[var(--hp-border-hi)] bg-[var(--hp-sidebar)] p-1.5 shadow-2xl">
            {years.map((y) => (
              <div key={y} className={`group flex items-center justify-between rounded-lg pr-1 transition-colors duration-150 ${
                y === year ? "bg-[var(--hp-primary)]" : "hover:bg-[var(--hp-tint-5)]"}`}>
                <button data-testid={`${testIdPrefix}year-option-${y}`}
                  onClick={() => { onYearChange(y); setOpen(false); }}
                  className={`flex-1 text-left px-3 py-2 text-sm font-medium ${y === year ? "text-[#04231a]" : "text-[var(--hp-text)]"}`}>
                  {y}
                </button>
                {allowDelete && years.length > 1 && (
                  <button data-testid={`delete-year-${y}`} onClick={(e) => handleDelete(e, y)}
                    className={`grid place-items-center h-7 w-7 rounded-md ${
                      y === year ? "text-[#04231a] hover:bg-black/10" : "text-[var(--hp-muted)] hover:text-[var(--hp-loss)] hover:bg-[var(--hp-loss)]/10"} transition-colors duration-150`}>
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                )}
              </div>
            ))}
            <div className="h-px bg-[var(--hp-border)] my-1" />
            <button data-testid={`${testIdPrefix}add-year-button`}
              onClick={() => { onAddYear(nextYear); setOpen(false); }}
              className="w-full flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium text-[var(--hp-profit)] hover:bg-[var(--hp-tint-5)] transition-colors duration-150">
              <Plus className="h-3.5 w-3.5" /> Add {nextYear}
            </button>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
